import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, ArrowLeft, X, Sparkles, Shield, BarChart3, ClipboardCheck, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const TOUR_STORAGE_KEY = 'spooker_tour_completed';

// Welcome modal slides
const WELCOME_SLIDES = [
  {
    icon: Sparkles,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    title: 'Welkom bij Spooker',
    subtitle: 'Je persoonlijke beveiligingsassistent',
    description: 'Spooker helpt je om de beveiliging van je organisatie te begrijpen en te verbeteren. Geen technisch jargon, gewoon duidelijke taal en concrete stappen.',
    features: [
      'Ontdek hoe goed je organisatie beveiligd is',
      'Krijg helder inzicht in risico\'s en zwakke plekken',
      'Ontvang concrete aanbevelingen die je direct kunt uitvoeren',
    ],
  },
  {
    icon: ClipboardCheck,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    title: 'In 3 stappen beveiligd',
    subtitle: 'Zo werkt het',
    description: 'Het platform begeleidt je stap voor stap naar een beter beveiligingsniveau.',
    steps: [
      { num: '1', title: 'Nulmeting', desc: 'Beantwoord vragen over je mensen, processen en technologie' },
      { num: '2', title: 'Inzicht', desc: 'Bekijk direct welke risico\'s en zwakke plekken er zijn' },
      { num: '3', title: 'Actie', desc: 'Volg de aanbevolen oplossingen om je beveiliging te versterken' },
    ],
  },
  {
    icon: Shield,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    title: 'Klaar om te beginnen',
    subtitle: 'We laten je even zien waar alles zit',
    description: 'Na deze korte rondleiding weet je precies hoe je het platform gebruikt. Je kunt de tour altijd opnieuw starten via Instellingen.',
  },
];

// Tooltip tour steps (multi-page guided tour)
const TOUR_STEPS = [
  {
    target: '[data-testid="main-sidebar"]',
    title: 'Je navigatie',
    description: 'Hier vind je alle onderdelen van je beveiliging: van analyse tot strategie.',
    position: 'right',
  },
  {
    target: '[data-testid="main-sidebar"]',
    title: 'Werk van boven naar beneden',
    description: 'Het menu volgt je beveiligingsreis: Analyse → Inzicht → Actie → Strategie. Begin bovenaan en werk naar beneden.',
    position: 'right',
    expandSidebar: true,
  },
  {
    target: '[data-testid="primary-action"]',
    title: 'Wat moet je nu doen?',
    description: 'Dit blok vertelt je altijd de volgende stap. Het "Aanbevolen" label geeft aan dat het systeem dit adviseert.',
    position: 'bottom',
  },
  {
    target: '[data-testid="priority-actions"], [data-testid="priority-action-0"]',
    title: 'Je aandachtspunten',
    description: 'Hier staan je urgentste taken. Het "Start hier" label markeert de belangrijkste. Werk ze één voor één af.',
    position: 'bottom',
  },
  {
    target: '[data-testid="quick-stats"], [data-testid="flow-progress"]',
    title: 'Je voortgang in één oogopslag',
    description: 'De statistieken en voortgangsbalk laten zien hoe ver je bent. Klik op een getal om direct naar die pagina te gaan.',
    position: 'top',
  },
  {
    target: '[data-testid="primary-cta"]',
    title: 'Klaar? Begin hier!',
    description: 'Klik op deze knop om je eerste nulmeting te starten. In ongeveer 40 minuten breng je je beveiliging in kaart.',
    position: 'right',
    isFinal: true,
  },
];

export const OnboardingTour = ({ forceStart = false, onComplete }) => {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'welcome' | 'tooltips' | 'done'
  const [slideIndex, setSlideIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [tooltipReady, setTooltipReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const overlayRef = useRef(null);

  // Check if tour should start
  useEffect(() => {
    if (forceStart) {
      setPhase('welcome');
      setSlideIndex(0);
      return;
    }
    // Check both localStorage and sessionStorage for robustness
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    const sessionDismissed = sessionStorage.getItem(TOUR_STORAGE_KEY);
    if (completed || sessionDismissed) {
      setPhase('idle');
      return;
    }
    // Auto-dismiss for automated tests / headless browsers
    if (navigator.webdriver) {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      sessionStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setPhase('idle');
      return;
    }
    setPhase('welcome');
    setSlideIndex(0);
  }, [forceStart]);

  // Position tooltip relative to target element (using viewport coords for fixed positioning)
  const positionTooltip = useCallback(() => {
    if (phase !== 'tooltips') return;
    const step = TOUR_STEPS[stepIndex];
    if (!step) return;

    const selectors = step.target.split(',').map(s => s.trim());
    let el = null;
    for (const sel of selectors) {
      el = document.querySelector(sel);
      if (el) break;
    }

    if (!el) {
      setTooltipReady(false);
      return;
    }

    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    setTooltipReady(true);

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Re-position after scroll settles
    setTimeout(() => {
      const r2 = el.getBoundingClientRect();
      setTooltipPos({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
    }, 400);
  }, [phase, stepIndex]);

  useEffect(() => {
    positionTooltip();
    const handleResize = () => positionTooltip();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [positionTooltip]);

  // Native DOM overlay - 4 panels around target create a "window" effect
  useLayoutEffect(() => {
    const PANEL_IDS = ['tour-panel-top', 'tour-panel-bottom', 'tour-panel-left', 'tour-panel-right'];
    const RING_ID = 'tour-highlight-ring';
    const baseStyle = 'position:fixed;background:rgba(0,0,0,0.6);z-index:99990;pointer-events:auto;transition:all 0.4s ease-out;';

    if (phase === 'tooltips') {
      // Inject pulse animation CSS if not exists
      if (!document.getElementById('tour-style')) {
        const style = document.createElement('style');
        style.id = 'tour-style';
        style.textContent = `
          @keyframes tour-pulse {
            0%, 100% { box-shadow: 0 0 0 4px rgba(0,0,0,0.15), 0 4px 24px rgba(0,0,0,0.2); }
            50% { box-shadow: 0 0 0 8px rgba(0,0,0,0.1), 0 4px 32px rgba(0,0,0,0.25); }
          }
        `;
        document.head.appendChild(style);
      }
      // Create panels if they don't exist - attach to <html> element for correct rendering
      const container = document.documentElement;
      PANEL_IDS.forEach(id => {
        if (!document.getElementById(id)) {
          const el = document.createElement('div');
          el.id = id;
          container.appendChild(el);
        }
      });
      if (!document.getElementById(RING_ID)) {
        const ring = document.createElement('div');
        ring.id = RING_ID;
        container.appendChild(ring);
      }

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (tooltipReady) {
        const pad = 10;
        const t = tooltipPos.top - pad;
        const l = tooltipPos.left - pad;
        const w = tooltipPos.width + pad * 2;
        const h = tooltipPos.height + pad * 2;

        // 4 panels surrounding the target, leaving a "hole"
        document.getElementById('tour-panel-top').style.cssText = `${baseStyle}top:0;left:0;width:${vw}px;height:${Math.max(0, t)}px;`;
        document.getElementById('tour-panel-bottom').style.cssText = `${baseStyle}top:${t + h}px;left:0;width:${vw}px;height:${Math.max(0, vh - t - h)}px;`;
        document.getElementById('tour-panel-left').style.cssText = `${baseStyle}top:${t}px;left:0;width:${Math.max(0, l)}px;height:${h}px;`;
        document.getElementById('tour-panel-right').style.cssText = `${baseStyle}top:${t}px;left:${l + w}px;width:${Math.max(0, vw - l - w)}px;height:${h}px;`;

        // Subtle highlight ring with pulse animation
        document.getElementById(RING_ID).style.cssText = `position:fixed;top:${t}px;left:${l}px;width:${w}px;height:${h}px;border-radius:12px;border:3px solid #000;box-shadow:0 0 0 4px rgba(0,0,0,0.15), 0 4px 24px rgba(0,0,0,0.2);z-index:99991;pointer-events:none;transition:top 0.4s ease-out, left 0.4s ease-out, width 0.4s ease-out, height 0.4s ease-out;animation:tour-pulse 2s ease-in-out infinite;`;
      } else {
        // No target found → full screen dark overlay
        document.getElementById('tour-panel-top').style.cssText = `${baseStyle}top:0;left:0;width:${vw}px;height:${vh}px;`;
        document.getElementById('tour-panel-bottom').style.cssText = `${baseStyle}display:none;`;
        document.getElementById('tour-panel-left').style.cssText = `${baseStyle}display:none;`;
        document.getElementById('tour-panel-right').style.cssText = `${baseStyle}display:none;`;
        document.getElementById(RING_ID).style.cssText = 'display:none;';
      }
    } else {
      PANEL_IDS.forEach(id => document.getElementById(id)?.remove());
      document.getElementById(RING_ID)?.remove();
      document.getElementById('tour-style')?.remove();
    }
  }, [phase, tooltipReady, tooltipPos]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      ['tour-panel-top', 'tour-panel-bottom', 'tour-panel-left', 'tour-panel-right', 'tour-highlight-ring', 'tour-style'].forEach(id =>
        document.getElementById(id)?.remove()
      );
    };
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    sessionStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setPhase('done');
    toast.success('Rondleiding voltooid! Je kunt altijd opnieuw starten via Instellingen.', {
      duration: 5000,
    });
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleSkip = () => {
    window.dispatchEvent(new CustomEvent('tour-collapse-sidebar'));
    completeTour();
    // Na overslaan: zorg dat gebruiker op het dashboard is
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const handleWelcomeNext = () => {
    if (slideIndex < WELCOME_SLIDES.length - 1) {
      setSlideIndex(slideIndex + 1);
    } else {
      // Transition to tooltip phase - make sure we're on dashboard
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      setPhase('tooltips');
      setStepIndex(0);
      // Small delay for DOM to settle
      setTimeout(() => positionTooltip(), 500);
    }
  };

  const handleWelcomePrev = () => {
    if (slideIndex > 0) setSlideIndex(slideIndex - 1);
  };

  const handleTooltipNext = () => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      const nextStep = TOUR_STEPS[stepIndex + 1];
      const currentStep = TOUR_STEPS[stepIndex];
      // Expand/collapse sidebar based on step requirements
      if (nextStep.expandSidebar) {
        window.dispatchEvent(new CustomEvent('tour-expand-sidebar'));
        // Extra delay for sidebar animation
        setStepIndex(stepIndex + 1);
        setTimeout(() => positionTooltip(), 600);
      } else {
        if (currentStep.expandSidebar) {
          window.dispatchEvent(new CustomEvent('tour-collapse-sidebar'));
        }
        setStepIndex(stepIndex + 1);
        setTimeout(() => positionTooltip(), 300);
      }
    } else {
      // Ensure sidebar collapses when tour ends
      if (TOUR_STEPS[stepIndex].expandSidebar) {
        window.dispatchEvent(new CustomEvent('tour-collapse-sidebar'));
      }
      completeTour();
    }
  };

  const handleTooltipPrev = () => {
    if (stepIndex > 0) {
      const prevStep = TOUR_STEPS[stepIndex - 1];
      const currentStep = TOUR_STEPS[stepIndex];
      if (prevStep.expandSidebar) {
        window.dispatchEvent(new CustomEvent('tour-expand-sidebar'));
        setStepIndex(stepIndex - 1);
        setTimeout(() => positionTooltip(), 600);
      } else {
        if (currentStep.expandSidebar) {
          window.dispatchEvent(new CustomEvent('tour-collapse-sidebar'));
        }
        setStepIndex(stepIndex - 1);
        setTimeout(() => positionTooltip(), 300);
      }
    }
  };

  if (phase === 'idle' || phase === 'done') return null;

  // PHASE 1: Welcome Modal
  if (phase === 'welcome') {
    const slide = WELCOME_SLIDES[slideIndex];
    const Icon = slide.icon;
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="onboarding-welcome">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
          {/* Skip button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors z-10"
            data-testid="onboarding-skip"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 pt-6">
            {WELCOME_SLIDES.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === slideIndex ? 'w-8 bg-black' : 'w-1.5 bg-neutral-200'
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <div className="px-8 pt-6 pb-8">
            <div className={`mx-auto w-14 h-14 rounded-2xl ${slide.iconBg} flex items-center justify-center mb-5`}>
              <Icon className={`h-7 w-7 ${slide.iconColor}`} strokeWidth={1.5} />
            </div>

            <h2 className="text-2xl font-semibold text-center tracking-[-0.02em]" data-testid="onboarding-slide-title">
              {slide.title}
            </h2>
            <p className="text-sm text-neutral-500 text-center mt-1">{slide.subtitle}</p>
            <p className="text-sm text-neutral-600 text-center mt-4 leading-relaxed">{slide.description}</p>

            {/* Features list */}
            {slide.features && (
              <div className="mt-6 space-y-3">
                {slide.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-neutral-600">
                    <div className="h-5 w-5 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                      <ChevronRight className="h-3 w-3 text-emerald-600" strokeWidth={2} />
                    </div>
                    {f}
                  </div>
                ))}
              </div>
            )}

            {/* Steps */}
            {slide.steps && (
              <div className="mt-6 space-y-3">
                {slide.steps.map((s) => (
                  <div key={s.num} className="flex items-start gap-4 p-3 rounded-xl bg-neutral-50 border border-neutral-100">
                    <div className="h-8 w-8 rounded-lg bg-black text-white flex items-center justify-center text-sm font-bold shrink-0">
                      {s.num}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black">{s.title}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer buttons */}
          <div className="px-8 pb-8 flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleWelcomePrev}
              className={slideIndex === 0 ? 'invisible' : ''}
              data-testid="onboarding-prev"
            >
              <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />
              Vorige
            </Button>
            <Button
              onClick={handleWelcomeNext}
              className="bg-black text-white hover:bg-neutral-800 px-6"
              data-testid="onboarding-next"
            >
              {slideIndex === WELCOME_SLIDES.length - 1 ? (
                <>
                  Start rondleiding
                  <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                </>
              ) : (
                <>
                  Volgende
                  <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // PHASE 2: Tooltip Overlay
  if (phase === 'tooltips') {
    const step = TOUR_STEPS[stepIndex];
    if (!step) return null;

    // Calculate tooltip position relative to target
    const getTooltipStyle = () => {
      if (!tooltipReady) {
        // Fallback: center in viewport when target not found
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 1,
        };
      }

      const gap = 16;
      const tooltipW = 340;
      const tooltipH = 200;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let top, left;
      const pos = step.position || 'right';

      if (pos === 'right') {
        top = tooltipPos.top + tooltipPos.height / 2 - tooltipH / 2;
        left = tooltipPos.left + tooltipPos.width + gap;
      } else if (pos === 'bottom') {
        top = tooltipPos.top + tooltipPos.height + gap;
        left = tooltipPos.left + tooltipPos.width / 2 - tooltipW / 2;
      } else if (pos === 'left') {
        top = tooltipPos.top + tooltipPos.height / 2 - tooltipH / 2;
        left = tooltipPos.left - tooltipW - gap;
      } else {
        top = tooltipPos.top - tooltipH - gap;
        left = tooltipPos.left + tooltipPos.width / 2 - tooltipW / 2;
      }

      // If right placement overflows, try left
      if (pos === 'right' && left + tooltipW > vw - gap) {
        left = tooltipPos.left - tooltipW - gap;
      }
      // If bottom placement overflows, try top
      if (pos === 'bottom' && top + tooltipH > vh - gap) {
        top = tooltipPos.top - tooltipH - gap;
      }

      // Clamp to viewport
      top = Math.max(gap, Math.min(top, vh - tooltipH - gap));
      left = Math.max(gap, Math.min(left, vw - tooltipW - gap));

      return { top, left, opacity: 1 };
    };

    return createPortal(
      <>
        {/* Tooltip card - only the tooltip is rendered via React, overlay is native DOM */}
        <div
          ref={overlayRef}
          data-testid={`tour-step-${stepIndex}`}
          id="tour-tooltip"
          className="bg-white rounded-xl shadow-2xl border border-neutral-200 p-5"
          style={{ position: 'fixed', ...getTooltipStyle(), width: 340, zIndex: 99995, transition: 'all 0.3s ease-out' }}
        >
          {/* Step counter + skip */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">
              Stap {stepIndex + 1} van {TOUR_STEPS.length}
            </span>
            <button
              onClick={handleSkip}
              className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
              data-testid="tour-skip"
            >
              Overslaan
            </button>
          </div>

          <h3 className="text-base font-semibold text-black">{step.title}</h3>
          <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">{step.description}</p>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTooltipPrev}
              className={stepIndex === 0 ? 'invisible' : 'text-xs'}
              data-testid="tour-prev"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              Vorige
            </Button>
            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-5 bg-black' : 'w-1.5 bg-neutral-200'}`} />
              ))}
            </div>
            <Button
              size="sm"
              onClick={handleTooltipNext}
              className="bg-black text-white hover:bg-neutral-800 text-xs"
              data-testid="tour-next"
            >
              {step.isFinal ? (
                <>
                  Afronden
                  <Sparkles className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                </>
              ) : (
                <>
                  Volgende
                  <ArrowRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                </>
              )}
            </Button>
          </div>
        </div>
      </>,
      document.body
    );
  }

  return null;
};

// Helper to reset tour (for settings page)
export const resetOnboardingTour = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
};

export const isTourCompleted = () => {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
};
